// useBreadcrumb.ts
import {useEffect, useMemo} from 'react';
import { useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../context/BreadCrumbContext';
import breadcrumbMapping from '../utils/mappings/breadcrumb_mapping';
import {useAppSelector} from "./useAppSelector";

interface OverrideBreadcrumbs {
    inner?: string[];
    base?: string;
}

const useDynamicBreadcrumb = (overrides?: OverrideBreadcrumbs) => {
    const { setBreadcrumb } = useBreadcrumb();
    const {choir} = useAppSelector(state => state.choirs);
    const location = useLocation();
    // check if location includes 'choir' and if so, get the choir name
    const hasChoir = location.pathname.includes('choir');

    const key = choir?.name;
    const overrideBase = overrides?.base;
    const overrideInnerKey = useMemo(
        () => (overrides?.inner || []).join('||'),
        [overrides?.inner]
    );
    const overrideInner = useMemo(
        () => (overrides?.inner ? [...overrides.inner] : undefined),
        [overrideInnerKey]
    );

    useEffect(() => {
        const mappedBreadcrumb = breadcrumbMapping(location.pathname.replace(/-/g, ' '));
        const defaultBreadcrumb = {
            base: mappedBreadcrumb.base,
            inner: [...mappedBreadcrumb.inner],
        };

        if (choir && key && hasChoir) {
            defaultBreadcrumb.inner[0] = key;
        }

        const finalBreadcrumb = {
            base: overrideBase ?? defaultBreadcrumb.base,
            inner: overrideInner ?? defaultBreadcrumb.inner,
        };

        setBreadcrumb((previous: any) => {
            const hasSameBase = previous?.base === finalBreadcrumb.base;
            const previousInner = previous?.inner || [];
            const nextInner = finalBreadcrumb.inner || [];
            const hasSameInner =
                previousInner.length === nextInner.length &&
                previousInner.every((value: string, index: number) => value === nextInner[index]);

            if (hasSameBase && hasSameInner) {
                return previous;
            }

            return finalBreadcrumb;
        });
    }, [choir, hasChoir, key, location.pathname, overrideBase, overrideInner, setBreadcrumb]);
};

export default useDynamicBreadcrumb;
